import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    LinearProgress,
    Popover,
    Snackbar,
    TableCell,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { Add, Delete, Edit, MiscellaneousServices } from '@mui/icons-material';
import { AppButton } from '../../../shared/components/AppButton';
import { AppSearch } from '../../../shared/components/AppSearch';
import { AppTable } from '../../../shared/components/AppTable';
import { AppTablePagination } from '../../../shared/components/AppTablePagination';
import { Loader } from '../../../shared/components/Loader';
import { usePermission } from '../../../shared/contexts/PermissionContext';
import { serviceService } from '../../../shared/services/serviceService';
import { ServiceItem } from '../../../shared/types/service';

export const Services: React.FC = () => {
    const { hasPermission, isSuperAdmin } = usePermission();

    const [services, setServices] = useState<ServiceItem[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [searchInput, setSearchInput] = useState('');
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'info' as 'info' | 'success' | 'error',
    });

    const [deleteAnchorEl, setDeleteAnchorEl] = useState<HTMLButtonElement | null>(null);
    const [selectedId, setSelectedId] = useState<string | null>(null);

    const [editOpen, setEditOpen] = useState(false);
    const [editId, setEditId] = useState<string>('');
    const [editDescription, setEditDescription] = useState('');
    const [editError, setEditError] = useState('');
    const [isUpdating, setIsUpdating] = useState(false);
    const [createOpen, setCreateOpen] = useState(false);
    const [createDescription, setCreateDescription] = useState('');
    const [createError, setCreateError] = useState('');
    const [isCreating, setIsCreating] = useState(false);

    const fetchServices = useCallback(async () => {
        setIsLoading(true);
        try {
            const res = await serviceService.list({
                page: currentPage + 1,
                limit: pageSize,
                search: searchInput || undefined,
            });
            const outerData = res?.data;
            const items =
                outerData?.data ||
                outerData?.items ||
                (outerData as any)?.services ||
                [];
            setServices(
                (Array.isArray(items) ? items : []).map((item: any) => ({
                    ...item,
                    _id: item._id ?? item.id,
                    description: String(item.description || '').trim(),
                })).filter((item: ServiceItem) => Boolean(item._id))
            );
        } catch {
            setServices([]);
            setSnackbar({ open: true, message: 'Error loading services', severity: 'error' });
        } finally {
            setIsLoading(false);
        }
    }, [currentPage, pageSize, searchInput]);

    useEffect(() => {
        fetchServices();
    }, [fetchServices]);

    const filteredServices = useMemo(() => {
        const q = searchInput.trim().toLowerCase();
        if (!q) return services;
        return services.filter((item) => item.description.toLowerCase().includes(q));
    }, [services, searchInput]);

    const paginatedServices = useMemo(() => {
        const start = currentPage * pageSize;
        return filteredServices.slice(start, start + pageSize);
    }, [filteredServices, currentPage, pageSize]);

    const openDelete = (e: React.MouseEvent<HTMLButtonElement>, id: string) => {
        setDeleteAnchorEl(e.currentTarget);
        setSelectedId(id);
    };

    const closeDelete = () => {
        setDeleteAnchorEl(null);
        setSelectedId(null);
    };

    const confirmDelete = async () => {
        if (!selectedId) return;
        try {
            await serviceService.delete(selectedId);
            setSnackbar({ open: true, message: 'Service deleted successfully', severity: 'success' });
            fetchServices();
        } catch {
            setSnackbar({ open: true, message: 'Error deleting service', severity: 'error' });
        } finally {
            closeDelete();
        }
    };

    const openEdit = (service: ServiceItem) => {
        setEditId(service._id);
        setEditDescription(service.description || '');
        setEditError('');
        setEditOpen(true);
    };

    const closeEdit = () => {
        if (isUpdating) return;
        setEditOpen(false);
        setEditId('');
        setEditDescription('');
        setEditError('');
    };

    const submitEdit = async () => {
        const nextDescription = editDescription.trim();
        if (!nextDescription) {
            setEditError('Description is required');
            return;
        }

        setIsUpdating(true);
        try {
            await serviceService.update(editId, { description: nextDescription });
            setSnackbar({ open: true, message: 'Service updated successfully', severity: 'success' });
            closeEdit();
            fetchServices();
        } catch (err: any) {
            setEditError(err?.response?.data?.message || 'Failed to update service');
        } finally {
            setIsUpdating(false);
        }
    };

    const openCreate = () => {
        setCreateDescription('');
        setCreateError('');
        setCreateOpen(true);
    };

    const closeCreate = () => {
        if (isCreating) return;
        setCreateOpen(false);
        setCreateDescription('');
        setCreateError('');
    };

    const submitCreate = async () => {
        const description = createDescription.trim();
        if (!description) {
            setCreateError('Description is required');
            return;
        }
        setIsCreating(true);
        try {
            await serviceService.create({ description });
            setSnackbar({ open: true, message: 'Service added successfully', severity: 'success' });
            closeCreate();
            fetchServices();
        } catch (err: any) {
            setCreateError(err?.response?.data?.message || 'Failed to add service');
        } finally {
            setIsCreating(false);
        }
    };

    return (
        <div className="flex flex-col w-full min-h-full bg-white">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-6 sm:py-8 sm:px-10 border-b border-gray-200 bg-white">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                    <MiscellaneousServices className="text-[#6366f1] w-8 h-8" />
                    <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight text-gray-900">
                        Services
                    </h2>
                </div>
                <div className="flex items-center gap-4 mt-4 sm:mt-0 flex-wrap">
                    <AppSearch
                        value={searchInput}
                        onChange={(v) => { setSearchInput(v); setCurrentPage(0); }}
                        onSearch={() => setCurrentPage(0)}
                        placeholder="Search services..."
                        className="w-full sm:w-64"
                    />
                    {(isSuperAdmin() || hasPermission('invoices.add')) && (
                        <AppButton
                            variant="primary"
                            startIcon={<Add />}
                            onClick={openCreate}
                            sx={{ px: 3, py: 1.5, borderRadius: '6px' }}
                        >
                            Add Service
                        </AppButton>
                    )}
                </div>
            </div>

            <div className="flex flex-1 flex-col p-6 sm:p-10 overflow-y-auto">
                <div className="flex flex-col w-full flex-1">
                    {isLoading && services.length > 0 && <LinearProgress className="mb-4" />}
                    {isLoading && services.length === 0 ? (
                        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                            <Loader />
                        </Box>
                    ) : (
                        <AppTable
                            head={
                                <TableRow className="bg-gray-100">
                                    <TableCell className="!font-semibold !text-gray-700">No</TableCell>
                                    <TableCell className="!font-semibold !text-gray-700">Service Description</TableCell>
                                    <TableCell className="!font-semibold !text-gray-700">Actions</TableCell>
                                </TableRow>
                            }
                            isEmpty={filteredServices.length === 0}
                            emptyColSpan={3}
                        >
                            {paginatedServices.map((item, index) => (
                                <TableRow key={item._id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                    <TableCell className="!text-gray-500 !text-sm">
                                        {currentPage * pageSize + index + 1}
                                    </TableCell>
                                    <TableCell className="!text-gray-900 !font-medium">
                                        {item.description || '-'}
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex items-center gap-1">
                                            {(isSuperAdmin() || hasPermission('invoices.update')) && (
                                                <Tooltip title="Edit">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => openEdit(item)}
                                                        className="text-blue-600 hover:bg-blue-50"
                                                    >
                                                        <Edit fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            {(isSuperAdmin() || hasPermission('invoices.delete')) && (
                                                <Tooltip title="Delete">
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => openDelete(e, item._id)}
                                                        className="text-red-500 hover:bg-red-50"
                                                    >
                                                        <Delete fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </AppTable>
                    )}
                </div>

                <AppTablePagination
                    totalRows={filteredServices.length}
                    page={currentPage}
                    pageSize={pageSize}
                    onPageChange={(_, p) => setCurrentPage(p - 1)}
                    onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(0); }}
                />
            </div>

            <Popover
                open={Boolean(deleteAnchorEl)}
                anchorEl={deleteAnchorEl}
                onClose={closeDelete}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{
                    sx: {
                        p: 0, mt: 1.5, width: 260,
                        boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)',
                        border: '1px solid #e5e7eb',
                        borderRadius: '12px',
                    },
                }}
            >
                <div className="p-4 bg-white">
                    <Typography className="font-semibold text-gray-900" sx={{ mb: 1 }}>
                        Delete Service?
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                        This service will be soft-deleted and hidden from suggestions.
                    </Typography>
                    <Divider sx={{ my: 1.5 }} />
                    <div className="flex justify-end gap-2">
                        <AppButton variant="secondary" size="small" onClick={closeDelete}>Cancel</AppButton>
                        <AppButton variant="danger" size="small" onClick={confirmDelete}>Delete</AppButton>
                    </div>
                </div>
            </Popover>

            <Dialog open={editOpen} onClose={closeEdit} fullWidth maxWidth="sm">
                <DialogTitle sx={{ fontWeight: 700 }}>Update Service</DialogTitle>
                <DialogContent sx={{ pt: '8px !important' }}>
                    <TextField
                        autoFocus
                        fullWidth
                        label="Service Description"
                        value={editDescription}
                        onChange={(e) => {
                            setEditDescription(e.target.value);
                            if (editError) setEditError('');
                        }}
                        error={!!editError}
                        helperText={editError}
                    />
                </DialogContent>
                <DialogActions sx={{ p: 2, pt: 0, gap: 1 }}>
                    <AppButton variant="secondary" onClick={closeEdit} disabled={isUpdating}>
                        Cancel
                    </AppButton>
                    <AppButton variant="primary" onClick={submitEdit} loading={isUpdating}>
                        Update
                    </AppButton>
                </DialogActions>
            </Dialog>

            <Dialog open={createOpen} onClose={closeCreate} fullWidth maxWidth="sm">
                <DialogTitle sx={{ fontWeight: 700 }}>Add Service</DialogTitle>
                <DialogContent sx={{ pt: '8px !important' }}>
                    <TextField
                        autoFocus
                        fullWidth
                        label="Service Description"
                        value={createDescription}
                        onChange={(e) => {
                            setCreateDescription(e.target.value);
                            if (createError) setCreateError('');
                        }}
                        error={!!createError}
                        helperText={createError}
                    />
                </DialogContent>
                <DialogActions sx={{ p: 2, pt: 0, gap: 1 }}>
                    <AppButton variant="secondary" onClick={closeCreate} disabled={isCreating}>
                        Cancel
                    </AppButton>
                    <AppButton variant="primary" onClick={submitCreate} loading={isCreating}>
                        Add
                    </AppButton>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert severity={snackbar.severity as any} variant="filled">
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </div>
    );
};
